import { useEffect, useRef } from 'react';
import { useMediaQuery } from '@mantine/hooks';

type Options = {
    delay?: number;
};

export function useAutoHideYarlButtons({ delay = 5000 }: Options = {}) {
    const isDesktop = useMediaQuery('(pointer: fine)');

    const idleTimeout = useRef<number | null>(null);
    const keyboardLock = useRef(false);

    useEffect(() => {
        if (!isDesktop) return;

        const getButtons = () =>
            document.querySelectorAll<HTMLElement>('.yarl__button');

        const show = () => {
            getButtons().forEach((el) =>
                el.classList.remove('is-hidden')
            );
        };

        const hide = () => {
            getButtons().forEach((el) =>
                el.classList.add('is-hidden')
            );
        };

        const clearIdleTimer = () => {
            if (idleTimeout.current) {
                window.clearTimeout(idleTimeout.current);
                idleTimeout.current = null;
            }
        };

        const startIdleTimer = () => {
            clearIdleTimer();
            idleTimeout.current = window.setTimeout(hide, delay);
        };

        /* ----------------------------
           Event handlers
        -----------------------------*/

        const onMouseMove = () => {
            // Mouse is the ONLY way to exit keyboard lock
            keyboardLock.current = false;
            show();
            startIdleTimer();
        };

        const onKeyDown = () => {
            // Enter keyboard lock
            keyboardLock.current = true;

            // Force-hide immediately (override YARL)
            hide();

            // Stop any timers
            clearIdleTimer();
        };

        document.addEventListener('mousemove', onMouseMove, { passive: true });
        document.addEventListener('keydown', onKeyDown);

        // Initial behavior: visible → auto-hide
        startIdleTimer();

        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('keydown', onKeyDown);
            clearIdleTimer();
        };
    }, [delay, isDesktop]);
}
